import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import useActivityTracking from '../../hooks/useActivityTracking.hooks';
import useAltClickReadout from '../../hooks/useAltClickReadout.hooks';
import {
  selectShowOrderSubmissionAlert,
  selectIsSessionTimeoutDialogOpen,
} from '../../redux/features/sessionBoundary/sessionBoundary.selectors';
import MainPageLayout from '../MainPageLayout';
import MainContent from '../MainContent';
import RestaurantStaffDialog from '../RestaurantStaff';
import SessionTimeoutDialog from '../SessionTimeoutDialog';
import { ReminderProvider } from '../../contexts/ReminderContext';
import {
  selectConfig,
  selectFeatureFlagHitl3,
} from '../../redux/features/config/config.selector';
import OrderSubmissionAlert from '../OrderSubmissionAlert';
import { setShowOrderSubmissionAlert } from '../../redux/features/sessionBoundary/sessionBoundary.slice';
import {
  selectIsCarPresent,
  selectIsWebSocketConnected,
} from '../../selectors/message';
import { clearMessages } from '../../reducers/messagingSlice';
import { useTaskLoadingMetricsContext } from '../../hooks/taskLoadingMetrics/TaskLoadingMetricsContext';
import { selectCurrentTask } from '../../redux/features/taskRouter/taskRouter.selector';
import { selectUserProfile } from '../../selectors/user';
import { selectActiveRestaurantCode } from '../../selectors/restaurant';
import { selectIsLoading } from '../../redux/features/loading/loading.selector';
import { MenuLoadingTimings, MenuLoadSource } from '../../reducers/menuSlice';

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  bodyClass: {
    height: `calc(100vh - ${spacing(8)})`,
  },
}));

const Body = () => {
  const { bodyClass } = useStyles();
  const isSessionTimeoutDialogOpen = useAppSelector(
    selectIsSessionTimeoutDialogOpen
  );
  const { onIdle, handleAgentSessionContinuation } = useActivityTracking();
  // HITL-1530: Alt+click a labelled control to read it out. Mounted here rather
  // than in MainPageLayout so it covers the legacy layout too — it is one
  // window listener, not a rendered thing.
  useAltClickReadout();
  const dispatch = useAppDispatch();

  const showOrderSubmissionAlert = useAppSelector(
    selectShowOrderSubmissionAlert
  );
  const config = useAppSelector(selectConfig);
  const isHitl3 = useAppSelector(selectFeatureFlagHitl3);
  const isCarPresent = useAppSelector(selectIsCarPresent);

  const {
    endAcknowledgementToPageTransition,
    endMenuLoading,
    endTaskLoadedComplete,
    sendTaskLoadedDependenciesFinishedMetric,
  } = useTaskLoadingMetricsContext();
  const currentTaskId = useAppSelector(selectCurrentTask);
  const userProfile = useAppSelector(selectUserProfile);
  const restaurantCode = useAppSelector(selectActiveRestaurantCode);
  const isEventsWSConnected = useAppSelector(selectIsWebSocketConnected);
  const isAudioWSConnected = useAppSelector(
    (state) => state.messages.isAudioWSConnected
  );
  const isLoading = useAppSelector(selectIsLoading);
  const menuLoadingTimings = useAppSelector(
    (state) => state.menu.menuLoadingTimings
  ) as MenuLoadingTimings;
  const eventWSConnectionDurationMs = useAppSelector(
    (state) => state.messages.eventWSConnectionDurationMs
  );
  const audioWSConnectionDurationMs = useAppSelector(
    (state) => state.messages.audioWSConnectionDurationMs
  );

  const lastTrackedTaskIdRef = useRef<string | null>(null);
  const pageTransitionTrackedRef = useRef(false);
  const eventsTrackedRef = useRef(false);
  const audioTrackedRef = useRef(false);
  const menuTrackedRef = useRef(false);
  const metricsSentRef = useRef(false);

  // Track previous states to detect transitions
  const prevEventsConnectedRef = useRef(false);
  const prevAudioConnectedRef = useRef(false);
  const prevLoadingRef = useRef(true);

  // Reset tracking refs when a new task comes in (but don't reset metrics - start times are set in useTaskRouterWS)
  useEffect(() => {
    if (currentTaskId && currentTaskId !== lastTrackedTaskIdRef.current) {
      lastTrackedTaskIdRef.current = currentTaskId;
      pageTransitionTrackedRef.current = false;
      eventsTrackedRef.current = false;
      audioTrackedRef.current = false;
      menuTrackedRef.current = false;
      metricsSentRef.current = false;
      // Reset prev state refs to current state so we can detect transitions
      prevEventsConnectedRef.current = isEventsWSConnected;
      prevAudioConnectedRef.current = isAudioWSConnected;
      prevLoadingRef.current = isLoading;
      // Note: Do NOT call resetMetrics() here - start times are set in useTaskRouterWS

      // Immediately mark page transition as complete (navigation happened)
      pageTransitionTrackedRef.current = true;
      endAcknowledgementToPageTransition();

      // If services are already connected, mark them as tracked
      if (isEventsWSConnected) {
        eventsTrackedRef.current = true;
      }
      if (isAudioWSConnected) {
        audioTrackedRef.current = true;
      }
      if (!isLoading) {
        menuTrackedRef.current = true;
        endMenuLoading(MenuLoadSource.DOWNLOADED);
      }
    }
  }, [
    currentTaskId,
    isEventsWSConnected,
    isAudioWSConnected,
    isLoading,
    endAcknowledgementToPageTransition,
    endMenuLoading,
  ]);

  // Track Events WebSocket connection transition (false -> true)
  useEffect(() => {
    if (
      currentTaskId &&
      !eventsTrackedRef.current &&
      !prevEventsConnectedRef.current &&
      isEventsWSConnected
    ) {
      eventsTrackedRef.current = true;
    }
    prevEventsConnectedRef.current = isEventsWSConnected;
  }, [currentTaskId, isEventsWSConnected]);

  // Track Audio WebSocket connection transition (false -> true)
  useEffect(() => {
    if (
      currentTaskId &&
      !audioTrackedRef.current &&
      !prevAudioConnectedRef.current &&
      isAudioWSConnected
    ) {
      audioTrackedRef.current = true;
    }
    prevAudioConnectedRef.current = isAudioWSConnected;
  }, [currentTaskId, isAudioWSConnected]);

  // Track Menu loading transition (true -> false)
  useEffect(() => {
    if (
      currentTaskId &&
      !menuTrackedRef.current &&
      prevLoadingRef.current &&
      !isLoading
    ) {
      menuTrackedRef.current = true;
      endMenuLoading(MenuLoadSource.DOWNLOADED);
    }
    prevLoadingRef.current = isLoading;
  }, [currentTaskId, isLoading, endMenuLoading]);

  // Send final metrics when all services are ready
  useEffect(() => {
    if (
      currentTaskId &&
      pageTransitionTrackedRef.current &&
      eventsTrackedRef.current &&
      audioTrackedRef.current &&
      menuTrackedRef.current &&
      !metricsSentRef.current
    ) {
      metricsSentRef.current = true;
      endTaskLoadedComplete(); // End task loaded complete timer right before sending
      sendTaskLoadedDependenciesFinishedMetric(
        currentTaskId,
        currentTaskId,
        restaurantCode || 'unknown',
        userProfile?.email || 'unknown',
        menuLoadingTimings.downloadDurationMs,
        menuLoadingTimings.processingDurationMs,
        menuLoadingTimings.unleashFlagDurationMs,
        menuLoadingTimings.processingStartTime &&
          menuLoadingTimings.downloadEndTime
          ? menuLoadingTimings.processingStartTime -
              menuLoadingTimings.downloadEndTime
          : 0,
        eventWSConnectionDurationMs,
        audioWSConnectionDurationMs
      );
    }
  }, [
    currentTaskId,
    restaurantCode,
    userProfile?.email,
    sendTaskLoadedDependenciesFinishedMetric,
    endTaskLoadedComplete,
    isEventsWSConnected,
    isAudioWSConnected,
    isLoading,
    menuLoadingTimings,
    eventWSConnectionDurationMs,
    audioWSConnectionDurationMs,
  ]);

  useEffect(() => {
    if (showOrderSubmissionAlert) {
      if (!isCarPresent) dispatch(clearMessages());

      const timer = setTimeout(() => {
        dispatch(setShowOrderSubmissionAlert(false));
      }, config.ORDER_SUBMISSION_ALERT_TIMEOUT);
      return () => {
        clearInterval(timer);
      };
    }
  }, [
    showOrderSubmissionAlert,
    config.ORDER_SUBMISSION_ALERT_TIMEOUT,
    isCarPresent,
  ]);
  return (
    <div data-testid="body" className={bodyClass}>
      <ReminderProvider>
        {/* hitl_3_0 is a per-restaurant Unleash flag (context = restaurantCode).
            With no restaurant selected (e.g. right after login/logout) the flag
            value is stale/meaningless, so fall back to the default layout until a
            restaurant is chosen — otherwise the 3-pane console shows with no
            active restaurant. */}
        {isHitl3 && restaurantCode ? <MainPageLayout /> : <MainContent />}
      </ReminderProvider>
      <RestaurantStaffDialog />
      {isSessionTimeoutDialogOpen && (
        <SessionTimeoutDialog
          timeoutCallback={onIdle}
          activeCallback={handleAgentSessionContinuation}
        />
      )}
      {showOrderSubmissionAlert && <OrderSubmissionAlert />}
    </div>
  );
};

export default Body;
